import { getApiBaseUrl } from '../lib/apiConfig';
import { ApiError } from '../types/api';
import { logger } from '../lib/logger';

const log = logger.createNamespace('API');

const TOKEN_KEY = 'towapp_access_token';
const REFRESH_TOKEN_KEY = 'towapp_refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export class ApiClientError extends Error {
  status: number;
  body: ApiError | null;

  constructor(status: number, body: ApiError | null) {
    super(body?.message || `HTTP ${status}`);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  if (!text) return undefined as unknown as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const url = `${getApiBaseUrl()}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (options.method === 'DELETE' && !options.body) {
    delete headers['Content-Type'];
  }

  const authenticated = Boolean(headers['Authorization']);
  log.debug('Request', { method, path, authenticated });

  const start = performance.now();

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const durationMs = Math.round(performance.now() - start);

  if (!response.ok) {
    let errorBody: ApiError | null = null;
    try {
      errorBody = await response.json();
    } catch {
      // no parseable error body
    }
    log.error('Request failed', new ApiClientError(response.status, errorBody), {
      method,
      path,
      status: response.status,
      durationMs,
      message: errorBody?.message,
    });
    throw new ApiClientError(response.status, errorBody);
  }

  log.debug('Response', { method, path, status: response.status, durationMs });
  return parseResponse<T>(response);
}

export async function authenticatedRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return request<T>(path, { ...options, headers });
}
